import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  Flag,
  LogIn,
  LogOut,
  Mail,
  MoreHorizontal,
  PanelRight,
  RotateCcw,
  Tag,
  Ticket,
  Trash2,
  UserRound,
  UserRoundPlus,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ConversationDetail } from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@nucleus-commerce/ui/components/avatar';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useChatAgents } from '../hooks/use-chat-agents';
import { useChatDepartments } from '../hooks/use-chat-departments';
import { useChatPresence } from '../hooks/use-chat-presence';
import {
  useAssignConversation,
  useConversationAction,
  useTrashConversation,
  useUpdateConversation,
} from '../hooks/use-conversation-mutations';
import { useNow } from '../hooks/use-now';
import { initialsOf } from '../lib/display';

import { ConvertTicketDialog } from './convert-ticket-dialog';
import { SlaSummary } from './sla-badge';

export interface ThreadHeaderProps {
  conversation: ConversationDetail;
  title: string;
  myId: string | undefined;
  /** The org-wide list route or the store's, for "back" on small screens and after trashing. */
  onBack: () => void;
  /** Opens the context pane where it is a sheet (below 1280 px). */
  onShowContext: () => void;
  assignOpen: boolean;
  onAssignOpenChange: (open: boolean) => void;
}

const PRIORITIES = [1, 2, 3, 4] as const;

type AgentStatus = 'online' | 'away' | 'offline';

/** Someone the conversation can be assigned to. */
interface AssignTarget {
  userId: string;
  name: string;
  /** The chat name shoppers see, when it is not the member's own name. */
  shownAs: string | null;
  avatarUrl: string | null;
  status: AgentStatus;
}

const STATUS_DOT: Record<AgentStatus, string> = {
  online: 'bg-emerald-500',
  away: 'bg-amber-500',
  offline: 'bg-muted-foreground/40',
};

function AssignTargetItem({ target, onSelect }: { target: AssignTarget; onSelect: () => void }) {
  const { t } = useTranslation('conversations');
  return (
    <DropdownMenuItem onSelect={onSelect}>
      <Avatar className="size-5">
        {target.avatarUrl ? <AvatarImage src={target.avatarUrl} alt="" /> : null}
        <AvatarFallback className="text-[9px]">{initialsOf(target.name)}</AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1 truncate">
        {target.name}
        {target.shownAs ? (
          <span className="text-muted-foreground ml-1 text-xs">
            {t('header.shownAs', { name: target.shownAs })}
          </span>
        ) : null}
      </span>
      <span className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[target.status])} aria-hidden />
      <span className="sr-only">{t(`header.agentStatus.${target.status}`)}</span>
    </DropdownMenuItem>
  );
}

/** Conversation actions: assign or transfer, join or leave, tags, priority, close or reopen, trash. */
export function ThreadHeader({
  conversation,
  title,
  myId,
  onBack,
  onShowContext,
  assignOpen,
  onAssignOpenChange,
}: ThreadHeaderProps) {
  const { t } = useTranslation('conversations');
  const { can } = usePermissions();
  const { storeId, id } = conversation;
  const writable = can('conversations.write');
  const assign = useAssignConversation(storeId, id);
  const update = useUpdateConversation(storeId, id);
  const join = useConversationAction(storeId, id, 'join');
  const leave = useConversationAction(storeId, id, 'leave');
  const close = useConversationAction(storeId, id, 'close');
  const reopen = useConversationAction(storeId, id, 'reopen');
  const trash = useTrashConversation(storeId, id);
  const presence = useChatPresence(storeId);
  const agents = useChatAgents(storeId);
  const departments = useChatDepartments(storeId);
  const [tags, setTags] = useState<string[] | null>(null);
  const [confirmTrash, setConfirmTrash] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const now = useNow();
  const ticket = conversation.kind === 'ticket';

  const participating = conversation.participants.some(
    (p) => p.userId === myId && p.leftAt === null,
  );
  const closed = conversation.status === 'closed';
  const status = new Map<string, AgentStatus>(
    (presence.data?.items ?? []).map((member) => [member.userId, member.status]),
  );
  // Assignable = the store's chat agents who can reply. Until that list answers (or if it
  // fails), the connected members and the participants stand in, as before LCW1.
  const targets: AssignTarget[] = agents.data
    ? agents.data
        .filter((agent) => agent.canReply && agent.userId !== myId)
        .map((agent) => ({
          userId: agent.userId,
          name: agent.name,
          shownAs:
            agent.chatDisplayName && agent.chatDisplayName !== agent.name
              ? agent.chatDisplayName
              : null,
          avatarUrl: agent.chatAvatarUrl,
          status: status.get(agent.userId) ?? 'offline',
        }))
    : [
        ...new Map<string, string>([
          ...(presence.data?.items ?? []).map((m): [string, string] => [m.userId, m.name]),
          ...conversation.participants.map((p): [string, string] => [p.userId, p.name]),
        ]).entries(),
      ]
        .filter(([userId]) => userId !== myId)
        .map(([userId, name]) => ({
          userId,
          name,
          shownAs: null,
          avatarUrl: null,
          status: status.get(userId) ?? 'offline',
        }));

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(`#${conversation.number}`);
      toast.success(t('header.copied'));
    } catch {
      toast.error(t('header.copyFailed'));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
      <Button
        variant="ghost"
        size="icon"
        className="size-8 lg:hidden"
        onClick={onBack}
        aria-label={t('header.back')}
      >
        <ArrowLeft aria-hidden />
      </Button>
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-semibold">{title}</h2>
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <span>#{conversation.number}</span>
          {conversation.kind !== 'chat' ? (
            <>
              <span aria-hidden>·</span>
              <span>{t(`kind.${conversation.kind}`)}</span>
            </>
          ) : null}
          <span aria-hidden>·</span>
          <span>{t(`status.${conversation.status}`)}</span>
          {conversation.assignee ? (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">
                {conversation.assignee.id === myId
                  ? t('header.assignedToYou')
                  : conversation.assignee.name}
              </span>
            </>
          ) : null}
        </p>
      </div>

      {writable ? (
        <>
          <DropdownMenu open={assignOpen} onOpenChange={onAssignOpenChange}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <UserRoundPlus aria-hidden />
                <span className="hidden sm:inline">{t('header.assign')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-96 w-64 overflow-y-auto">
              {conversation.assignee?.id !== myId && myId ? (
                <DropdownMenuItem onSelect={() => assign.mutate({ userId: myId })}>
                  <UserRound aria-hidden />
                  {t('header.assignToMe')}
                </DropdownMenuItem>
              ) : null}
              {targets.length > 0 ? (
                <DropdownMenuLabel>{t('header.teammates')}</DropdownMenuLabel>
              ) : null}
              {targets.map((target) => (
                <AssignTargetItem
                  key={target.userId}
                  target={target}
                  onSelect={() => assign.mutate({ userId: target.userId })}
                />
              ))}
              {(departments.data?.length ?? 0) > 0 ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>{t('header.transferDepartment')}</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {(departments.data ?? []).map((department) => (
                      <DropdownMenuItem
                        key={department.id}
                        disabled={department.id === conversation.departmentId}
                        onSelect={() => assign.mutate({ departmentId: department.id })}
                      >
                        {department.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : null}
              {conversation.assignee ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => assign.mutate({ userId: null })}>
                    {t('header.unassign')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>

          {closed ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => reopen.mutate()}
              disabled={reopen.isPending}
            >
              <RotateCcw aria-hidden />
              <span className="hidden sm:inline">{t('header.reopen')}</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => close.mutate()}
              disabled={close.isPending}
            >
              <CheckCircle2 aria-hidden />
              <span className="hidden sm:inline">{t('header.close')}</span>
            </Button>
          )}
        </>
      ) : null}

      <Button
        variant="ghost"
        size="icon"
        className="size-8 xl:hidden"
        onClick={onShowContext}
        aria-label={t('header.showContext')}
      >
        <PanelRight aria-hidden />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8" aria-label={t('header.more')}>
            <MoreHorizontal aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {writable ? (
            participating ? (
              <DropdownMenuItem onSelect={() => leave.mutate()}>
                <LogOut aria-hidden />
                {t('header.leave')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => join.mutate()}>
                <LogIn aria-hidden />
                {t('header.join')}
              </DropdownMenuItem>
            )
          ) : null}
          {writable ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Flag aria-hidden />
                {t('header.priority')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={String(conversation.priority)}
                  onValueChange={(value) => update.mutate({ priority: Number(value) })}
                >
                  {PRIORITIES.map((priority) => (
                    <DropdownMenuRadioItem key={priority} value={String(priority)}>
                      {t(`priority.${priority}`)}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : null}
          {writable && !ticket ? (
            <DropdownMenuItem onSelect={() => setConvertOpen(true)}>
              <Ticket aria-hidden />
              {t('header.convert')}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => void copyNumber()}>
            <Copy aria-hidden />
            {t('header.copyNumber')}
          </DropdownMenuItem>
          {conversation.customer ? (
            <DropdownMenuItem asChild>
              <Link
                to="/s/$storeId/customers/$customerId"
                params={{ storeId, customerId: conversation.customer.id }}
              >
                <UserRound aria-hidden />
                {t('header.openCustomer')}
              </Link>
            </DropdownMenuItem>
          ) : null}
          <Can permission="conversations.manage">
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => setConfirmTrash(true)}>
              <Trash2 aria-hidden />
              {t('header.trash')}
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>

      {ticket ? (
        <div className="w-full space-y-1">
          <p className="flex min-w-0 items-center gap-1.5 text-sm">
            <Mail className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
            <span className="text-muted-foreground shrink-0">{t('header.subject')}</span>
            <span className="truncate font-medium">
              {conversation.subject ?? t('header.noSubject')}
            </span>
          </p>
          <SlaSummary conversation={conversation} now={now} />
        </div>
      ) : null}

      <div className="flex w-full flex-wrap items-center gap-1">
        {conversation.priority < 3 ? (
          <Badge
            variant={conversation.priority === 1 ? 'destructive' : 'secondary'}
            className="h-5 text-[10px]"
          >
            {t(`priority.${conversation.priority}`)}
          </Badge>
        ) : null}
        {conversation.tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="h-5 text-[10px] font-normal">
            {tag}
          </Badge>
        ))}
        {writable ? (
          <Popover
            open={tags !== null}
            onOpenChange={(open) => {
              if (open) {
                setTags(conversation.tags);
                return;
              }
              const next = tags;
              setTags(null);
              if (next && next.join('\u0000') !== conversation.tags.join('\u0000'))
                update.mutate({ tags: next });
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-6 px-1.5 text-xs"
              >
                <Tag className="size-3" aria-hidden />
                {conversation.tags.length > 0 ? t('header.editTags') : t('header.addTags')}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-2">
              <Label htmlFor="conversation-tags">{t('header.tags')}</Label>
              <TagInput
                id="conversation-tags"
                value={tags ?? []}
                onChange={setTags}
                max={30}
                placeholder={t('header.tagsPlaceholder')}
                removeLabel={(tag) => t('header.removeTag', { tag })}
              />
              <p className="text-muted-foreground mt-2 text-xs">{t('header.tagsHint')}</p>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>

      {writable && !ticket ? (
        <ConvertTicketDialog
          conversation={conversation}
          open={convertOpen}
          onOpenChange={setConvertOpen}
        />
      ) : null}

      <AlertDialog open={confirmTrash} onOpenChange={setConfirmTrash}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('trash.title', { number: conversation.number })}</AlertDialogTitle>
            <AlertDialogDescription>{t('trash.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('trash.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => trash.mutate(undefined, { onSuccess: onBack })}
              className="bg-destructive hover:bg-destructive/90 text-white"
            >
              {t('trash.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
